import { useState } from 'react';
import { message } from 'antd';
import { useAppDispatch } from './redux';
import { fetchVisualizationDataThunk } from '../../store/thunks/sessionThunks';
import { getVisualizationConfig } from '../../config/visualizationAgents';
import { extractJson } from '../../utils/jsonExtractor';
import { exportVisualizationToExcel } from '../../utils/excelExport';
import { type AgentConfig } from '../../types/agent.types';
import { type VisualizationData, type VisualizationMode } from '../../types/visualization.types';

interface UseVisualizationReturn {
  vizLoading: boolean;
  vizModalData: VisualizationData | null;
  handleVisualize: (mode: VisualizationMode) => Promise<void>;
  closeVizModal: () => void;
}

export function useVisualization(
  activeAgent: AgentConfig | null,
): UseVisualizationReturn {
  const dispatch = useAppDispatch();
  const [vizLoading, setVizLoading] = useState(false);
  const [vizModalData, setVizModalData] = useState<VisualizationData | null>(null);

  const handleVisualize = async (mode: VisualizationMode) => {
    if (!activeAgent || vizLoading) return;

    const config = getVisualizationConfig(activeAgent.name);
    if (!config) return;

    const prompt =
      mode === 'excel' && config.excelPrompt
        ? config.excelPrompt
        : config.dataPrompt;

    setVizLoading(true);
    try {
      const rawText = await dispatch(
        fetchVisualizationDataThunk(prompt),
      ).unwrap();

      const data = extractJson<VisualizationData>(rawText);
      const hasTraces = data?.traces?.length;
      const hasRawData = data?.rawData?.length;

      if (!hasTraces && !hasRawData) {
        message.error('The agent did not return valid visualization data. Please try again.');
        return;
      }

      if (mode === 'excel') {
        exportVisualizationToExcel(data, data.title || activeAgent.name);
      } else {
        setVizModalData(data);
      }
    } catch {
      message.error('Failed to fetch visualization data from the agent. Please try again.');
    } finally {
      setVizLoading(false);
    }
  };

  return {
    vizLoading,
    vizModalData,
    handleVisualize,
    closeVizModal: () => setVizModalData(null),
  };
}
